/**
 * Shared Table Configuration Utilities
 *
 * Common functions and constants for table column configurations
 * across different sections of the Chariot UI.
 *
 * @example
 * ```tsx
 * import { COMMON_COLUMN_WIDTHS, createTableConfig } from '@/utils/tableConfig.util';
 *
 * export const MY_TABLE_WIDTHS = {
 *   status: COMMON_COLUMN_WIDTHS.status,
 *   name: 250,
 * } as const;
 * ```
 */

import { Columns } from '@/components/table/types';

/**
 * Common column widths used across multiple tables
 *
 * @remarks
 * These values are used as building blocks for table-specific configurations.
 * Use these constants when possible to maintain visual consistency.
 */
export const COMMON_COLUMN_WIDTHS = {
  // Action columns
  checkbox: 40,
  icon: 40,
  view: 40,
  actions: 60,

  // Status/indicator columns
  status: 90,
  health: 70,

  // Severity columns (for risk indicators)
  severityCritical: 80,
  severityHigh: 80,
  severityMedium: 80,
  severityLow: 80,
  severityInfo: 80,

  // Common data columns
  timestamp: 180,
  email: 200,
  name: 250,
  description: 300,
  type: 120,
  origin: 200,
  country: 200,
} as const;

/**
 * Default row heights by table density
 *
 * @remarks
 * Choose the appropriate height based on the content density:
 * - compact: For tables with simple text content
 * - standard: Most common use case
 * - comfortable: For tables with rich content or multiple lines
 */
export const DEFAULT_ROW_HEIGHTS = {
  compact: 48,
  standard: 64,
  comfortable: 80,
} as const;

/**
 * Table configuration interface
 *
 * @template TWidths - Object type containing column width definitions
 */
export interface TableConfig<TWidths extends Record<string, number>> {
  widths: TWidths;
  defaultColumnOrder: readonly (keyof TWidths)[];
  hiddenColumns: readonly (keyof TWidths)[];
  storageKey: string;
  defaultRowHeight: number;
}

/**
 * Helper to create sortable column config from columns
 *
 * @param columns - Array of column definitions
 * @param options - Configuration options
 * @returns Sortable column configuration for Table component
 *
 * @example
 * ```tsx
 * const columns = getMyColumns();
 * const config = createSortableConfig(columns, {
 *   disabledColumns: ['id'],
 *   defaultColumns: ['name'],
 * });
 * ```
 */
export function createSortableConfig<T>(
  columns: Columns<T>,
  options: {
    disabledColumns?: string[];
    defaultColumns?: string[];
  } = {}
) {
  const { disabledColumns = [], defaultColumns = [] } = options;

  return {
    fixed: [],
    active: columns.map(({ id, label }) => ({
      id,
      label,
      disabled:
        disabledColumns.includes(id as string) ||
        defaultColumns.includes(id as string),
      isDefault: defaultColumns.includes(id as string),
    })),
    available: [],
  };
}

/**
 * Helper to build table config object with type safety
 *
 * @param config - Table configuration parameters
 * @returns Immutable table configuration object
 *
 * @example
 * ```tsx
 * export const MY_TABLE_CONFIG = createTableConfig({
 *   widths: MY_TABLE_WIDTHS,
 *   defaultColumnOrder: ['id', 'name', 'status'],
 *   hiddenColumns: [],
 *   storageKey: generateStorageKey('myTable'),
 *   defaultRowHeight: DEFAULT_ROW_HEIGHTS.standard,
 * });
 * ```
 */
export function createTableConfig<T extends Record<string, number>>(config: {
  widths: T;
  defaultColumnOrder: readonly (keyof T)[];
  hiddenColumns?: readonly (keyof T)[];
  storageKey: string;
  defaultRowHeight?: number;
}): TableConfig<T> {
  return {
    widths: config.widths,
    defaultColumnOrder: config.defaultColumnOrder,
    hiddenColumns: config.hiddenColumns || [],
    storageKey: config.storageKey,
    defaultRowHeight: config.defaultRowHeight || DEFAULT_ROW_HEIGHTS.standard,
  } as const;
}

/**
 * Type guard to check if a column should be expandable
 *
 * @param width - Fixed width value (if any)
 * @param minWidth - Minimum width value (if any)
 * @returns true if column should be expandable
 */
export function isExpandableColumn(width?: number, minWidth?: number): boolean {
  return width === undefined && minWidth !== undefined;
}

/**
 * Generate storage key with version suffix
 *
 * @param section - Section name (e.g., 'seeds', 'assets')
 * @param variant - Optional variant name (e.g., 'domain', 'ip')
 * @param version - Version number (default: 1)
 * @returns Formatted storage key for localStorage persistence
 *
 * @example
 * ```tsx
 * generateStorageKey('seeds', 'domain', 1)  // 'seeds-domain-v1'
 * generateStorageKey('assets')              // 'assets-v1'
 * ```
 */
export function generateStorageKey(
  section: string,
  variant?: string,
  version = 1
): string {
  const base = variant ? `${section}-${variant}` : section;
  return `${base}-v${version}`;
}

/**
 * Validate that column order matches width keys
 *
 * @param widths - Column width configuration
 * @param order - Column order array
 * @returns true if all order keys exist in widths
 *
 * @remarks
 * Use this in tests to ensure column configuration is valid
 */
export function validateColumnOrder<T extends Record<string, number>>(
  widths: T,
  order: readonly (keyof T)[]
): boolean {
  const widthKeys = Object.keys(widths);
  return order.every(key => widthKeys.includes(key as string));
}
